import { StrictMode, useEffect, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { AuthProvider, useAuth } from '@/features/auth';
import { router } from './router';
import { setAuthErrorCallback } from '@/services/api/client';
import './styles/index.css';

// Create React Query client at top level to share between providers
const queryClient = new QueryClient();

// Component that sets up the auth error callback with router access
function AuthErrorSetup({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Set up auth error callback for API client
    setAuthErrorCallback(() => {
      // Clear all query cache
      queryClient.clear();

      // Navigate to login with a message using router instance
      router.navigate({
        to: '/login',
        search: {
          message: 'Your session has expired. Please log in again.',
          reason: 'session_expired'
        },
      });
    });
  }, []);

  return <>{children}</>;
}

function AppWithRouter() {
  const auth = useAuth();
  return <RouterProvider router={router} context={{ auth }} />;
}

/**
 * Resolve the LaunchDarkly provider. A "context" is the data object describing
 * who is evaluating flags (here an anonymous browser user until login). When no
 * client-side ID is configured we fall back to a passthrough wrapper so the app
 * still renders — flags just return their code defaults.
 */
async function resolveLDProvider(): Promise<(props: { children: ReactNode }) => ReactNode> {
  const clientSideID = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_SIDE_ID?.trim();

  if (!clientSideID) {
    console.warn(
      'LaunchDarkly: VITE_LAUNCHDARKLY_CLIENT_SIDE_ID not set; feature flags disabled (using code defaults).'
    );
    return ({ children }) => <>{children}</>;
  }

  return asyncWithLDProvider({
    clientSideID,
    context: { kind: 'user', key: 'anonymous', anonymous: true },
    timeout: 5,
  });
}

// Render app
const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

void (async () => {
  const LDProvider = await resolveLDProvider();

  createRoot(rootElement).render(
    <StrictMode>
      <LDProvider>
        <QueryClientProvider client={queryClient}>
          <AuthErrorSetup>
            <AuthProvider>
              <AppWithRouter />
            </AuthProvider>
          </AuthErrorSetup>
        </QueryClientProvider>
      </LDProvider>
    </StrictMode>
  );
})();
